import { createContext, useContext } from 'react'
import { InMemoryLocalStorage } from './in-memory-local-storage'

export const LocalStorageContext = createContext<Storage | null>(null)

/**
 * The browser's localStorage, or null when there is none (native, SSR) or reading it throws:
 * blocked site data ("block all cookies") makes the `localStorage` getter throw a SecurityError.
 */
export function browserLocalStorage(): Storage | null {
  try {
    return (globalThis as { localStorage?: Storage }).localStorage ?? null
  } catch {
    return null
  }
}

let memoryFallback: Storage | undefined

/** The browser's localStorage, or one per-load in-memory Storage when it is unavailable. */
export function localStorageOrMemory(): Storage {
  return browserLocalStorage() ?? (memoryFallback ??= new InMemoryLocalStorage())
}

export function useLocalStorageContext(): Storage {
  const ctx = useContext(LocalStorageContext)
  if (ctx) return ctx
  if (typeof window !== 'undefined') return localStorageOrMemory()
  throw new Error(
    'No localStorage available (SSR without LocalStorageProvider)',
  )
}
